import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, HelpCircle, CalendarDays, Clock, Edit, Users, FileText } from "lucide-react";
import { DashboardConfigUrl } from "../../../types";

const iconMap: Record<string, React.ReactNode> = {
    // simple defaults; fallback handled below
    leave: <CalendarDays className="w-5 h-5 text-gray-500" />,
    timeoff: <CalendarDays className="w-5 h-5 text-gray-500" />,
    correction: <Edit className="w-5 h-5 text-gray-500" />,
    attendance: <Users className="w-5 h-5 text-gray-500" />,
    break: <Clock className="w-5 h-5 text-gray-500" />,
    help: <HelpCircle className="w-5 h-5 text-gray-500" />,
};

const defaultGenericIcon = <FileText className="w-5 h-5 text-gray-500" />;

const getIconFor = (icon?: string) => {
    const key = (icon || "").toLowerCase().trim();
    if (!key) return defaultGenericIcon; // generic default
    for (const k of Object.keys(iconMap)) {
        if (key.includes(k)) return iconMap[k];
    }
    return defaultGenericIcon; // fallback to generic if unknown
};

type WebQuickActionsTileProps = {
    links: DashboardConfigUrl[];
};

const WebQuickActionsTile: React.FC<WebQuickActionsTileProps> = ({ links }) => {
    const navigate = useNavigate();

    if (!links || links.length === 0) {
        return null;
    }

    const sortedLinks = [...links].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

    const isAbsoluteUrl = (url: string): boolean => /^[a-z][a-z\d+\-.]*:\/\//i.test(url);

    const handleClick = (link: DashboardConfigUrl) => {
        const { url, target } = link;

        if (!url) return;

        if (target === "_blank") {
            window.open(url, "_blank", "noopener,noreferrer");
            return;
        }

        if (isAbsoluteUrl(url)) {
            window.location.assign(url);
            return;
        }

        navigate(url);
    };

    return (
        <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="divide-y divide-gray-200">
                {sortedLinks.map((link, idx) => (
                    <button
                        key={`${link.code || link.title}-${idx}`}
                        onClick={() => handleClick(link)}
                        className="w-full flex items-center justify-between py-3 focus:outline-none hover:bg-gray-50 transition-colors rounded-md px-2"
                        title={link.title}
                    >
                        <div className="flex items-center gap-3">
                            <span className="flex items-center justify-center bg-gray-100 rounded-md h-9 w-9">
                                {getIconFor(link.icon)}
                            </span>
                            <span className="text-gray-800 text-sm md:text-base text-left">
                                {link.title}
                            </span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                    </button>
                ))}
            </div>
        </div>
    );
};

export default WebQuickActionsTile;
